import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { canModify } from '../utils/permissions';
import { getOpportunityById, clearOpportunityStageHistory } from '../services/api';
import { useOpportunityStore, useOpportunityStoreBase, type Opportunity as StoreOpportunity } from '../stores/opportunityStore';
import { ConfirmModal, RowAction, StatusDot, Amount, btnPrimary } from '@redpanda/ui';
import { stageTone } from '../utils/statusTone';
import { Role } from '@redpanda/types';
import { toast } from 'react-hot-toast';
import OpportunityDialog from '../components/OpportunityDialog';
import { useStageStore } from '../stores/stageStore';
import { useDataChanged } from '../utils/dataEvents';
import { createDetailCache } from '../utils/detailCache';
import { toIntlLocale, listReturnUrl, RESTORE_SCROLL_STATE } from '@redpanda/utils';

interface StageHistory {
  id: string;
  fromStageId?: string;
  toStageId: string;
  changedAt: string;
  userId: string;
  notes?: string;
}

// 與 opportunityStore 同一份定義，只加上詳情 API 才有的階段歷史
interface Opportunity extends StoreOpportunity {
  stageHistory?: StageHistory[];
}

const opportunityDetailCache = createDetailCache<{ data: Opportunity }>('opportunityDetailsCache');

const OpportunityDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const userRole = user?.role as Role;

  const { opportunities } = useOpportunityStore();
  const { stages, fetchStages } = useStageStore();

  // 階段歷史要顯示階段名稱（store 有快取）
  useEffect(() => {
    fetchStages();
  }, [fetchStages]);
  const stageName = (stageId?: string) =>
    stages.find(s => s.id === stageId)?.name || t('customers.detail.unknownStage', { ns: 'pages' });
  // 先顯示快取（上次看這個商機時存的，或列表 store 裡的），再背景向後端更新；都沒有才顯示載入中
  const [opportunity, setOpportunity] = useState<Opportunity | null>(() =>
    opportunityDetailCache.get(id)?.data ?? (opportunities.find(opp => opp.id === id) as Opportunity | undefined) ?? null
  );
  const [detailsLoading, setDetailsLoading] = useState<boolean>(!opportunity);
  const [error, setError] = useState<string | null>(null);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // 載入商機詳情
  useEffect(() => {
    const loadOpportunity = async () => {
      if (!id) {
        navigate('/opportunities');
        return;
      }

      try {
        // 有快取或列表 store 的資料就先顯示，一律背景向後端更新（含階段歷史）
        // 注意：不要為了找單一商機去抓整份列表、也不要讓 effect 依賴列表陣列——
        // 商機不在第一頁時會「抓列表 → 陣列換參照 → effect 重跑 → 再抓」無限循環
        const fromStore = opportunityDetailCache.get(id)?.data ?? (useOpportunityStoreBase.getState().items.find(opp => opp.id === id) as Opportunity | undefined);
        if (fromStore) {
          setOpportunity(fromStore);
        } else {
          setDetailsLoading(true);
        }

        let opportunityData: Opportunity;
        try {
          const response = await getOpportunityById(id);
          opportunityData = response.data;
        } catch (error) {
          if (fromStore) {
            // 背景載入失敗時保留已顯示的資料
            console.warn('Failed to load detailed opportunity data:', error);
            return;
          }
          throw error;
        }

        setOpportunity(opportunityData as Opportunity);
        
        opportunityDetailCache.set(id, { data: opportunityData });
      } catch (error: any) {
        console.error('載入商機詳情失敗:', error);
        setError(t('opportunities.detail.loadFailed', { ns: 'pages' }));
        toast.error(t('opportunities.detail.loadFailed', { ns: 'pages' }));
      } finally {
        setDetailsLoading(false);
      }
    };

    if (isAuthenticated && !isUserLoading) {
      loadOpportunity();
    }
  }, [id, navigate, isAuthenticated, isUserLoading, t]);

  // 編輯對話框是樂觀更新（關閉時 API 還沒完成）：API 成功後才會通知，收到再向後端重抓（含階段歷史）並更新快取
  useDataChanged('opportunities', async () => {
    if (!id) return;
    try {
      const response = await getOpportunityById(id);
      const opportunityData = response.data;
      setOpportunity(opportunityData);
      opportunityDetailCache.set(id, { data: opportunityData });
    } catch (error: any) {
      // 商機已被刪除（例：在別的分頁刪除）時不提示錯誤
      if (error?.response?.status !== 404) {
        console.error('重新載入商機詳情失敗:', error);
        toast.error(t('opportunities.detail.loadFailed', { ns: 'pages' }));
      }
    }
  });

  // 格式化日期時間
  const formatDateTime = (dateString: string) => {
    const locale = toIntlLocale(i18n.language);
    return new Date(dateString).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 清除階段變更歷史
  const handleClearHistory = async () => {
    if (!opportunity) return;

    setIsClearing(true);
    try {
      const response = await clearOpportunityStageHistory(opportunity.id);
      
      // 清除前端顯示的歷史記錄
      const updatedOpportunity = {
        ...opportunity,
        stageHistory: []
      };
      setOpportunity(updatedOpportunity);
      
      opportunityDetailCache.set(opportunity.id, { data: updatedOpportunity });
      
      const message = response.data?.message || t('opportunities.detail.historyCleared', { ns: 'pages' });
      toast.success(message);
      setShowClearHistoryModal(false);
    } catch (error: any) {
      console.error('清除階段變更歷史失敗:', error);
      const errorMessage = error.response?.data?.error || t('opportunities.detail.clearHistoryFailed', { ns: 'pages' });
      toast.error(errorMessage);
    } finally {
      setIsClearing(false);
    }
  };

  if (isUserLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center text-red-600">
        {t('opportunities.detail.pleaseLogin', { ns: 'pages' })}
      </div>
    );
  }

  if (detailsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600">
        {error}
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="text-center text-red-600">
        {t('opportunities.detail.notFound', { ns: 'pages' })}
      </div>
    );
  }

  // 左欄「標籤：值」清單（§3-13：不要每個區塊再包一張卡）
  const facts: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: t('common.forms.customer'),
      value: (
        <Link to={`/customers/${opportunity.customer.id}`} className="text-fg hover:text-accent transition-colors">
          {opportunity.customer.name}
          {opportunity.customer.company && <span className="block text-[13px] text-fg-secondary">{opportunity.customer.company}</span>}
        </Link>
      ),
    },
    { label: t('opportunities.detail.value', { ns: 'pages' }), value: <span className="tabular-nums"><Amount value={opportunity.value} /></span> },
    {
      label: t('opportunities.detail.currentStage', { ns: 'pages' }),
      value: (
        <span className="inline-flex items-center gap-2">
          <StatusDot tone={stageTone(opportunity.currentStage.probability)} label={opportunity.currentStage.name} />
          <span className="text-[13px] text-fg-secondary tabular-nums">{Math.round(opportunity.currentStage.probability * 100)}%</span>
        </span>
      ),
    },
    {
      label: t('opportunities.detail.owner', { ns: 'pages' }),
      value: (
        <>
          {opportunity.createdByName}
          {opportunity.createdByRole && (
            <span className="block text-[13px] text-fg-secondary">
              {/* SUPER_ADMIN → superAdmin 等翻譯 key */}
              {t(`common.roles.${opportunity.createdByRole.toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`, { defaultValue: opportunity.createdByRole })}
            </span>
          )}
        </>
      ),
    },
    { label: t('opportunities.detail.createdAt', { ns: 'pages' }), value: <span className="tabular-nums">{formatDateTime(opportunity.createdAt)}</span> },
    { label: t('opportunities.detail.updatedAt', { ns: 'pages' }), value: <span className="tabular-nums">{formatDateTime(opportunity.updatedAt)}</span> },
    ...(opportunity.closedAt
      ? [{ label: t('opportunities.detail.closedAt', { ns: 'pages' }), value: <span className="tabular-nums">{formatDateTime(opportunity.closedAt)}</span> }]
      : []),
  ];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁首：麵包屑＋名稱（頁標題）＋關鍵狀態＋1 顆主要按鈕（§3-13） */}
      <nav className="mb-1 text-[13px] text-fg-secondary">
        <Link to={listReturnUrl('/opportunities')} state={RESTORE_SCROLL_STATE} className="hover:text-fg transition-colors">{t('opportunities.list.title', { ns: 'pages' })}</Link>
        <span className="mx-1.5 text-fg-muted">/</span>
      </nav>
      <div className="mb-4">
        <div className="flex items-baseline justify-between gap-4">
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white truncate">{opportunity.title}</h1>
            <StatusDot tone={stageTone(opportunity.currentStage.probability)} label={opportunity.currentStage.name} />
          </div>
          {canModify(opportunity, userRole, userContext?.user?.id) && (
            <button onClick={() => setShowEditModal(true)} className={btnPrimary}>
              {t('opportunities.detail.editOpportunity', { ns: 'pages' })}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <dl className="lg:col-span-1 self-start bg-surface rounded-lg border border-line divide-y divide-line text-sm">
          {facts.map(f => (
            <div key={f.label} className="grid grid-cols-[6rem_1fr] gap-3 px-5 py-3">
              <dt className="text-fg-secondary">{f.label}</dt>
              <dd className="text-fg min-w-0">{f.value}</dd>
            </div>
          ))}
        </dl>

        {/* 描述＋階段歷史：一個容器、細線分隔 */}
        <div className="lg:col-span-2 bg-surface rounded-lg border border-line divide-y divide-line">
          {opportunity.description && (
            <section className="px-5 py-4">
              <h2 className="text-base font-semibold text-fg mb-2">{t('opportunities.detail.description', { ns: 'pages' })}</h2>
              <p className="text-sm text-fg-secondary whitespace-pre-wrap">{opportunity.description}</p>
            </section>
          )}

          <section className="px-5 py-4">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-base font-semibold text-fg">{t('opportunities.detail.stageHistory', { ns: 'pages' })}</h2>
              {(userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && opportunity.stageHistory && opportunity.stageHistory.length > 0 && (
                <RowAction tone="danger" onClick={() => setShowClearHistoryModal(true)}>
                  {t('opportunities.detail.clearHistory', { ns: 'pages' })}
                </RowAction>
              )}
            </div>

            {opportunity.stageHistory && opportunity.stageHistory.length > 0 ? (
              <ol className="space-y-3">
                {opportunity.stageHistory.map((history) => (
                  <li key={history.id} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-fg">
                        {history.fromStageId ? t('opportunities.detail.stageProgression', { ns: 'pages' }) : t('opportunities.detail.opportunityCreated', { ns: 'pages' })}
                        <span className="ml-2 text-fg-secondary">
                          {history.fromStageId ? `${stageName(history.fromStageId)} → ${stageName(history.toStageId)}` : stageName(history.toStageId)}
                        </span>
                        <span className="ml-2 text-[13px] text-fg-secondary tabular-nums">{formatDateTime(history.changedAt)}</span>
                      </p>
                      {history.notes && (
                        <p className="text-[13px] text-fg-secondary mt-0.5">{history.notes}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="py-4 text-sm text-fg-secondary">{t('opportunities.detail.noStageHistory', { ns: 'pages' })}</p>
            )}
          </section>
        </div>
      </div>

      {/* 清除歷史確認：改用共用 ConfirmModal（R15） */}
      <ConfirmModal
        isOpen={showClearHistoryModal}
        onClose={() => setShowClearHistoryModal(false)}
        onConfirm={handleClearHistory}
        title={t('opportunities.detail.confirmClearHistory', { ns: 'pages' })}
        message={t('opportunities.detail.confirmClearHistoryMessage', { ns: 'pages' })}
        confirmText={t('opportunities.detail.confirmClear', { ns: 'pages' })}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isClearing}
        loadingText={t('opportunities.detail.clearing', { ns: 'pages' })}
        t={t}
      />

      {/* 編輯商機對話框 */}
      <OpportunityDialog
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        opportunity={opportunity}
      />
    </div>
  );
};

export default OpportunityDetailPage;